import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { BehaviorSubject, Observable, interval, Subscription, ReplaySubject, tap, catchError, throwError, shareReplay, map, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';


@Injectable({
  providedIn: 'root'
})
export class ScienceService {
  private baseUrl = 'https://www.cognitiex.com/py/assistance/';
  private landmarksSubject = new ReplaySubject<any>(1);

  // MediaPipe et Webcam
  private poseLandmarker: PoseLandmarker | null = null;
  private isInitialized = false;
  private predictionSubject = new BehaviorSubject<any>(null);
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private isStreamingSubject = new BehaviorSubject<boolean>(false);
  
  // Session Management
  private isActiveSubject = new BehaviorSubject<boolean>(false);
  private currentExerciseSubject = new BehaviorSubject<string>('');
  private correctionsSubject = new BehaviorSubject<any[]>([]);
  private confidenceSubject = new BehaviorSubject<number>(0);

  private frameInterval: Subscription | null = null;
  private frameCount = 0;
  private sendFrameEvery = 5; // Envoyer toutes les 5 frames

  private lastCoords: number[] | null = null;
  private lastVels: number[] | null = null;
  public mappings$!: Observable<any>;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeMediaPipe();
      this.mappings$ = this.http.get(this.mappingsUrl).pipe(
        shareReplay(1),
        catchError(err => {
          console.error('Erreur chargement JSON mappings:', err);
          return of({});
        })
      );
    }
  }


  private async initializeMediaPipe(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm'
      );
      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task'
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });
      this.isInitialized = true;

    } catch (error) {
      console.error('Erreur initialisation MediaPipe:', error);
      throw error;
    }
  }


  // ================== WEBCAM MANAGEMENT ==================
  async startWebcam(videoElement: HTMLVideoElement): Promise<void> {
    // Vérifier qu'on est bien côté navigateur
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: false
      });

      this.videoElement = videoElement;
      videoElement.srcObject = this.stream;

      // Attendre que la vidéo soit complètement chargée
      await new Promise((resolve) => {
        videoElement.onloadeddata = resolve;
      });

      await videoElement.play();
      this.isStreamingSubject.next(true);
    } catch (error) {
      console.error('Erreur accès webcam:', error);
      throw error;
    }
  }

  startSession(
    videoElement: HTMLVideoElement,
    userLevel: string,
    currentExercise: string,
    userId?: number
  ): void {
    this.isActiveSubject.next(true);
    this.frameInterval = interval(100).subscribe(() => {
      this.processCurrentFrame(videoElement, userLevel, currentExercise, userId);
    });
  }

  stopSession(): void {
    this.isActiveSubject.next(false);
    this.frameInterval?.unsubscribe();
    this.frameInterval = null;
    this.lastCoords = null;
    this.lastVels = null;
  }

  // Getters pour les observables
  getLandmarks(): Observable<any> {
    return this.landmarksSubject.asObservable();
  }

  getSessionStatus(): Observable<boolean> {
    return this.isActiveSubject.asObservable();
  }

  getCurrentExercise(): Observable<string> {
    return this.currentExerciseSubject.asObservable();
  }

  getCorrections(): Observable<any[]> {
    return this.correctionsSubject.asObservable();
  }

  getConfidence(): Observable<number> {
    return this.confidenceSubject.asObservable();
  }

  sendPoseData(features: number[], userLevel: string, userId?: number): Observable<any> {
    const payload = { features, user_level: userLevel, user_id: userId };
    console.log('Payload envoyé vers /pose/analyze/:', payload);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.baseUrl}pose/analyze/`, payload, { headers });
  }

  updatePredictions(prediction: any): void {
    this.predictionSubject.next(prediction);
  }
  stopWebcam(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.isStreamingSubject.next(false);
  }

  getStreamingStatus(): Observable<boolean> {
    return this.isStreamingSubject.asObservable();
  }

  getCurrentFrame(): HTMLVideoElement | null {
    return this.videoElement;
  }

  async selectCamera(deviceId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.stream) {
      this.stopWebcam();
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId } },
      audio: false
    });

    if (this.videoElement) {
      this.videoElement.srcObject = this.stream;
      await this.videoElement.play();
    }
  }

  async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    if (!isPlatformBrowser(this.platformId)) {
      return [];
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'videoinput');
  }

  // ================== POSE PROCESSING ==================
  processFrame(imageElement: HTMLImageElement | HTMLVideoElement): any {
    if (!this.poseLandmarker || !this.isInitialized) return null;

    const results = this.poseLandmarker.detect(imageElement);
    if (results.landmarks && results.landmarks.length > 0) {
      this.landmarksSubject.next(results.landmarks[0]);
      return results.landmarks[0];
    }
    return null;
  }
  /*
  // ================== FEATURE EXTRACTION ==================
  extractFeatures(landmarks: any[]): number[] {
    if (!landmarks || landmarks.length === 0) return [];

    const coords = this.extractCoordinates(landmarks); // 99 valeurs
    const angles = this.computeAngles(landmarks);      // 4 valeurs
    const distances = this.computeDistances(landmarks); // 2 valeurs

    return [...coords, ...angles, distances[0]]; // 99 + 4 + 1 = 104
  }*/

  private mappingsUrl = '/model_mappings.json'; // chemin public
  getExercisePoints(exercise: string): Observable<number[]> {
    return this.mappings$.pipe(
      map(m => {
        console.log('Available exercises:', Object.keys(m.exercise_points || {}));
        const points = m.exercise_points?.[exercise] ?? [];
        console.log(`Exercise points for "${exercise}":`, points);
        return points;
      })
    );
  }

  private calculateAngle(a: any, b: any, c: any): number {
    const v1 = { x: a.x - b.x, y: a.y - b.y };
    const v2 = { x: c.x - b.x, y: c.y - b.y };
    const dot = v1.x * v2.x + v1.y * v2.y;
    const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
    const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
    const cosAngle = dot / (mag1 * mag2);
    return Math.acos(Math.max(-1, Math.min(1, cosAngle))) * (180 / Math.PI);
  }

  private euclideanDistance(p1: any, p2: any): number {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  private padFeatures(features: number[], targetDim: number = 108): number[] {
    if (features.length === 0) {
      console.error('Features array is empty! Returning zeros.');
      return new Array(targetDim).fill(0);
    }

    if (features.length === targetDim) {
      return features; // Déjà la bonne dimension
    }

    if (features.length > targetDim) {
      console.warn(`Features length (${features.length}) exceeds target (${targetDim}). Truncating.`);
      return features.slice(0, targetDim);
    }

    // Padding avec des zéros pour atteindre la dimension cible
    const paddingSize = targetDim - features.length;
    const padding = new Array(paddingSize).fill(0);
    console.log(`Padding ${features.length} features to ${targetDim} (adding ${paddingSize} zeros)`);
    return [...features, ...padding];
  }

  // ================== EXTRACTION DE FEATURES CORRIGÉE ==================

  extractFeatures(
    landmarks: any[],
    currentExercise: string,
    selectedIdx: number[],
    lastCoords: number[] | null,
    lastVels: number[] | null
  ): { features: number[], lastCoords: number[], lastVels: number[] } {

    // CORRECTION 1: Gestion des cas d'erreur avec retour de 108 zéros
    if (!landmarks?.length || !selectedIdx.length) {
      console.warn('No landmarks or selectedIdx available');
      console.warn('Landmarks:', landmarks?.length || 0, 'Selected indices:', selectedIdx?.length || 0);
      return {
        features: new Array(108).fill(0), // Au lieu d'un tableau vide !
        lastCoords: [],
        lastVels: []
      };
    }

    try {
      // 1. Coordonnées 3D sélectionnées
      const coords = selectedIdx.flatMap((i: number) => [landmarks[i].x, landmarks[i].y, landmarks[i].z]);

      // 2. Angles biomécaniques (7 angles)
      const angles = [
        this.calculateAngle(landmarks[12], landmarks[14], landmarks[16]),
        this.calculateAngle(landmarks[11], landmarks[13], landmarks[15]),
        this.calculateAngle(landmarks[24], landmarks[26], landmarks[28]),
        this.calculateAngle(landmarks[23], landmarks[25], landmarks[27]),
        this.calculateAngle(landmarks[11], landmarks[23], landmarks[24]),
        this.calculateAngle(landmarks[12], landmarks[23], landmarks[24]),
        this.calculateAngle(landmarks[23], landmarks[11], landmarks[12])
      ];

      // 3. Distances clés
      const distances = [
        this.euclideanDistance(landmarks[11], landmarks[12]),
        this.euclideanDistance(landmarks[23], landmarks[24])
      ];

      // 4. Vitesses et accélérations
      const vels = lastCoords
        ? coords.map((c: number, i: number) => (c - lastCoords[i]) * 10)
        : coords.map(() => 0);

      const accs = lastVels
        ? vels.map((v: number, i: number) => (v - lastVels[i]) * 10)
        : vels.map(() => 0);

      // 5. Concaténation des features brutes
      const rawFeatures = [...coords, ...angles, ...distances, ...vels, ...accs];

      // CORRECTION 2: Application du padding pour normaliser à 108
      const features = this.padFeatures(rawFeatures, 108);

      console.log(`Exercise: ${currentExercise}, Raw features: ${rawFeatures.length}, Final: ${features.length}`);

      return { features, lastCoords: coords, lastVels: vels };

    } catch (error) {
      console.error('Error in extractFeatures:', error);
      return {
        features: new Array(108).fill(0),
        lastCoords: [],
        lastVels: []
      };
    }
  }

  private extractCoordinates(landmarks: any[]): number[] {
    const coords: number[] = [];
    landmarks.forEach(landmark => {
      coords.push(landmark.x, landmark.y, landmark.z);
    });
    return coords;
  }

  private computeAngles(landmarks: any[]): number[] {
    const angles: number[] = [];

    // Angle coude droit (12, 14, 16)
    angles.push(this.calculateAngle(landmarks[12], landmarks[14], landmarks[16]));
    // Angle coude gauche (11, 13, 15)
    angles.push(this.calculateAngle(landmarks[11], landmarks[13], landmarks[15]));
    // Angle genou droit (24, 26, 28)
    angles.push(this.calculateAngle(landmarks[24], landmarks[26], landmarks[28]));
    // Angle genou gauche (23, 25, 27)
    angles.push(this.calculateAngle(landmarks[23], landmarks[25], landmarks[27]));

    return angles;
  }

  private computeDistances(landmarks: any[]): number[] {
    const distances: number[] = [];

    // Distance épaules
    const shoulderDist = this.euclideanDistance(landmarks[11], landmarks[12]);
    distances.push(shoulderDist);

    // Distance hanches
    const hipDist = this.euclideanDistance(landmarks[23], landmarks[24]);
    distances.push(hipDist);

    return distances;
  }



  // ================== SESSION MANAGEMENT ==================
  /*
  stopSession(): void {
    this.isActiveSubject.next(false);

    if (this.frameInterval) {
      this.frameInterval.unsubscribe();
      this.frameInterval = null;
    }
    
    this.frameCount = 0;
  }*/

  private processCurrentFrame(
    videoElement: HTMLVideoElement,
    userLevel: string,
    currentExercise: string,
    userId?: number
  ): void {

    // CORRECTION 3: Vérifications renforcées
    if (!videoElement) {
      console.error('Video element is null');
      return;
    }

    if (videoElement.readyState < 2) {
      console.warn('Video not ready, readyState:', videoElement.readyState);
      return;
    }

    if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
      console.warn('Video dimensions are 0');
      return;
    }

    if (!this.poseLandmarker || !this.isInitialized) {
      console.error('MediaPipe not initialized');
      return;
    }

    if (!currentExercise) {
      console.error('Current exercise is empty');
      return;
    }

    // Comptage des frames
    this.frameCount++;
    if (this.frameCount % this.sendFrameEvery !== 0) return;

    // CORRECTION 4: Inférence MediaPipe avec gestion d'erreur
    try {
      const timestamp = performance.now();
      const results = this.poseLandmarker.detectForVideo(videoElement, timestamp);

      if (!results.landmarks?.length) {
        console.warn('No landmarks detected by MediaPipe');
        return;
      }

      const landmark = results.landmarks[0];
      this.landmarksSubject.next(landmark);

      // CORRECTION 5: Vérification des exercise points avant extraction
      this.getExercisePoints(currentExercise).subscribe({
        next: selectedIdx => {
          if (!selectedIdx.length) {
            console.error('No exercise points found for:', currentExercise);
            return;
          }

          const { features, lastCoords, lastVels } = this.extractFeatures(
            landmark, currentExercise, selectedIdx, this.lastCoords, this.lastVels
          );

          this.lastCoords = lastCoords;
          this.lastVels = lastVels;

          // Vérification finale de la dimension
          if (features.length !== 108) {
            console.error(`DIMENSION ERROR: Expected 108, got ${features.length}`);
            return;
          }

          this.sendPoseData(features, userLevel, userId).subscribe({
            next: response => {
              this.currentExerciseSubject.next(response.exercise);
              this.correctionsSubject.next(response.corrections);
              this.confidenceSubject.next(response.confidence);
              this.updatePredictions(response);
            },
            error: error => {
              console.error('Erreur envoi API:', error);
            }
          });
        },
        error: error => {
          console.error('Erreur getExercisePoints:', error);
        }
      });

    } catch (error) {
      console.error('Erreur MediaPipe detection:', error);
    }
  }


  // Observables pour les composants

  // ================== API COMMUNICATION ==================
  sendMessage(message: string): Observable<any> {
    const payload = { message };
    return this.http.post<any>(`${this.baseUrl}chat/`, payload);
  }
  // science.service.ts

  getPredictions(): Observable<any> {
    return this.predictionSubject.asObservable();
  }

}
